import { NavLink } from 'react-router-dom'
import { ChevronsLeft, ChevronsRight } from 'lucide-react'
import { useAuth } from '@/lib/auth-context'
import { navItemsForRole, resolveNavLabel } from './nav-items'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { auth } = useAuth()
  if (!auth) return null

  const items = navItemsForRole(auth.role)

  return (
    <aside
      className={cn(
        'sticky top-0 hidden h-screen shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-150 md:flex',
        collapsed ? 'w-16' : 'w-56',
      )}
    >
      <div className={cn('flex items-center gap-2 px-3 py-3', collapsed && 'justify-center px-2')}>
        {!collapsed && (
          <span className="font-heading text-sm leading-snug font-semibold text-sidebar-foreground">
            El mágico mundo del carrusel de los niños
          </span>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          className={cn('shrink-0', !collapsed && 'ml-auto')}
          onClick={onToggle}
          aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        >
          {collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />}
        </Button>
      </div>

      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 py-2">
        {items.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-sidebar-foreground/75 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                isActive && 'bg-sidebar-accent font-semibold text-sidebar-primary',
                collapsed && 'justify-center px-0',
              )
            }
            title={collapsed ? resolveNavLabel(item, auth.role) : undefined}
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span className="absolute inset-y-1 left-0 w-1 rounded-full bg-sidebar-primary" aria-hidden />
                )}
                <item.icon className={cn('size-4.5 shrink-0', item.color)} />
                {!collapsed && <span className="truncate">{resolveNavLabel(item, auth.role)}</span>}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </aside>
  )
}
